import React, { useMemo, useCallback, useState } from "react";
import { Edit, X } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";

import SponsorSection from "../Sponsor/SponsorSection";
import SponsoredSection from "../Sponsor/SponsoredSection";
import SummarySection from "../Sponsor/SummarySection";
import {
  useAddSponsorMutation,
  useRemoveSponsorMutation,
} from "../../Services/modules/sponsors";
import { useGetUserSponsorsQuery } from "../../Services/modules/users";
import LoadingState from "../generic/LoadingState";
import ErrorState from "../generic/ErrorState";

export default function ViewSponsorsDialog({ user, open, onOpenChange }) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedSponsors, setSelectedSponsors] = useState([]);
  const [selectedSponsored, setSelectedSponsored] = useState([]);

  const {
    data: sponsorsData,
    error,
    isLoading,
    refetch,
  } = useGetUserSponsorsQuery(user?.uid, {
    skip: !open || !user?.uid,
  });

  const [removeSponsor, { isLoading: isRemovingSponsor }] =
    useRemoveSponsorMutation();
  const [addSponsor, { isLoading: isAddingSponsor }] = useAddSponsorMutation();

  const sponsor = useMemo(() => sponsorsData?.sponsor, [sponsorsData?.sponsor]);
  const sponsoredUsers = useMemo(
    () => sponsorsData?.sponsoredUsers || [],
    [sponsorsData?.sponsoredUsers]
  );
  const sponsoredCount = useMemo(
    () => sponsorsData?.sponsoredCount || 0,
    [sponsorsData?.sponsoredCount]
  );

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleDeleteSponsors = useCallback(
    async (sponsorsToDelete) => {
      try {
        const deletePromises = sponsorsToDelete.map((sponsor) =>
          removeSponsor({
            uid: user?.uid,
            sponsorUid: sponsor.uid || sponsor.id,
            sponsoredUid: user?.uid,
          })
        );

        await Promise.all(deletePromises);
        setSelectedSponsors([]);
        refetch();
      } catch (error) {
        console.error("Erreur lors de la suppression des parrains:", error);
      }
    },
    [user?.uid, removeSponsor, refetch]
  );

  const handleDeleteSponsored = useCallback(
    async (sponsoredToDelete) => {
      try {
        const deletePromises = sponsoredToDelete.map((sponsored) =>
          removeSponsor({
            uid: user?.uid,
            sponsorUid: user?.uid,
            sponsoredUid: sponsored?.uid,
          })
        );

        await Promise.all(deletePromises);
        setSelectedSponsored([]);
        refetch();
      } catch (error) {
        console.error("Erreur lors de la suppression des filleuls:", error);
      }
    },
    [user?.uid, removeSponsor, refetch]
  );

  const handleAddSponsors = useCallback(
    async (sponsorsToAdd) => {
      try {
        const addPromises = sponsorsToAdd.map((sponsor) =>
          addSponsor({
            uid: user?.uid,
            sponsorUid: sponsor.uid || sponsor.id,
            sponsoredUid: user?.uid,
          })
        );

        await Promise.all(addPromises);
        refetch();
      } catch (error) {
        console.error("Erreur lors de l'ajout des parrains:", error);
      }
    },
    [user?.uid, addSponsor, refetch]
  );

  const handleAddSponsored = useCallback(
    async (sponsoredToAdd) => {
      try {
        const addPromises = sponsoredToAdd.map((sponsored) =>
          addSponsor({
            uid: user?.uid,
            sponsorUid: user?.uid,
            sponsoredUid: sponsored.uid || sponsored.id,
          })
        );

        await Promise.all(addPromises);
        refetch();
      } catch (error) {
        console.error("Erreur lors de l'ajout des filleuls:", error);
      }
    },
    [user?.uid, addSponsor, refetch]
  );

  const handleCancelEdit = useCallback(() => {
    setSelectedSponsors([]);
    setSelectedSponsored([]);
    setIsEditMode(false);
  }, []);

  const actionButtons = useMemo(() => {
    if (!isEditMode) {
      return (
        <Button
          onClick={() => setIsEditMode(true)}
          className="flex gap-2 items-center"
        >
          <Edit className="w-4 h-4" />
          Mode édition
        </Button>
      );
    }

    return (
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={handleCancelEdit}
          disabled={isRemovingSponsor || isAddingSponsor}
          className="flex gap-2 items-center"
        >
          <X className="w-4 h-4" />
          Terminer l'édition
        </Button>
      </div>
    );
  }, [isEditMode, handleCancelEdit, isRemovingSponsor, isAddingSponsor]);

  const dialogContent = useMemo(() => {
    if (isLoading) {
      return <LoadingState text="Chargement des sponsors..." />;
    }

    if (error) {
      return (
        <ErrorState
          message="Erreur lors du chargement des sponsors"
          onRetry={handleRetry}
        />
      );
    }

    if (!sponsorsData) return null;

    return (
      <div className="space-y-6">
        <SponsorSection
          sponsor={sponsor}
          isEditMode={isEditMode}
          selectedSponsors={selectedSponsors}
          onSelectionChange={setSelectedSponsors}
          onDeleteSponsors={handleDeleteSponsors}
          onAddSponsors={handleAddSponsors}
        />
        <SponsoredSection
          sponsoredUsers={sponsoredUsers}
          sponsoredCount={sponsoredCount}
          isEditMode={isEditMode}
          selectedSponsored={selectedSponsored}
          onSelectionChange={setSelectedSponsored}
          onDeleteSponsored={handleDeleteSponsored}
          onAddSponsored={handleAddSponsored}
        />
        <SummarySection
          sponsor={sponsor}
          sponsoredUsers={sponsoredUsers}
          sponsoredCount={sponsoredCount}
        />
      </div>
    );
  }, [
    isLoading,
    error,
    handleRetry,
    sponsorsData,
    sponsor,
    sponsoredUsers,
    sponsoredCount,
    isEditMode,
    selectedSponsors,
    selectedSponsored,
    handleDeleteSponsors,
    handleDeleteSponsored,
    handleAddSponsors,
    handleAddSponsored,
  ]);

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex gap-2 justify-start items-center">
            <span>Gestion des sponsors</span>
            {actionButtons}
          </DialogTitle>
        </DialogHeader>
        {dialogContent}
      </DialogContent>
    </Dialog>
  );
}
